import { Secondary, Text, TextLink } from '@cinch-labs/design-system'
import { useCallback } from 'react'
import styles from './find-a-store.module.css'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import { useMarshallExplanationDrawer } from './marshall-explanation-drawer'
import { useManufacturerApprovedDrawer } from './manufacturer-approved-drawer'
import { StoreWithDistance } from '../../hooks/types'
import { FindAStore, toMiles } from './find-a-store'

export const ServicingFindAStore: React.FC = () => {
  const { trackEvent } = useAnalytics()
  const {
    dispatch,
    postcode: ctxPostCode,
    step,
    under60Months,
    vehicleMake,
  } = useMaintenanceContext()

  const { setShowMarshallExplanationDrawer } = useMarshallExplanationDrawer()
  const {
    setShowManufacturerApprovedDrawer,
    setNearestOemStoreName,
    setNearestOemStoreDistance,
  } = useManufacturerApprovedDrawer()

  const handleNearestOemStoreChange = useCallback(
    (store: StoreWithDistance | undefined) => {
      if (store) {
        setNearestOemStoreName(store.store.name)
        setNearestOemStoreDistance(toMiles(store.distance))
      } else {
        setNearestOemStoreName('')
        setNearestOemStoreDistance(undefined)
      }
    },
    [setNearestOemStoreName, setNearestOemStoreDistance],
  )

  const selectStore = useCallback(
    (storeWithDistance: StoreWithDistance, postcode: string) => {
      const { store, distance } = storeWithDistance
      trackEvent({
        eventName: `Select Store`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'store_selected',
            label: store.canBookAppointment
              ? 'booking_journey'
              : 'enquiry_journey',
            type: store.name,
            value: toMiles(distance),
          },
        },
      })

      datadogRum.addAction('Cinch Servicing: Store Selected', {
        cinchServicing: true,
        step,
        page: window.location.pathname,
        storeName: store.name,
        storeId: store.id,
        postcode,
        canBookAppointment: store.canBookAppointment,
        isNonCag: store.isNonCag,
      })

      dispatch({
        type: 'SET_STORE',
        name: store.name,
        id: store.id,
        displayName: store.displayName,
      })
      dispatch({
        type: 'SET_POSTCODE',
        postcode,
      })
      dispatch({
        type: 'SET_CAN_BOOK_APPOINTMENT',
        canBookAppointment: store.canBookAppointment,
      })
      dispatch({
        type: 'SET_DEALER_ID',
        dealerId: store.dealerId,
      })
      dispatch({
        type: 'SET_DEALER_CODE',
        dealerCode: store.dealerCode,
      })
      dispatch({ type: 'SET_NON_CAG', isNonCag: !!store.isNonCag })
      dispatch({
        type: 'RESET_BOOKING_DATETIME',
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [dispatch, step, trackEvent],
  )

  const isProd = process.env['STAGE_NAME'] === 'production'
  const descriptionElement = isProd ? (
    <Text>
      Enter your postcode to find your nearest service centre from over 100{' '}
      <TextLink
        onClick={() => {
          trackEvent({
            eventName: `Marshall explanation drawer opened`,
            type: TrackingEventTypes.ADOBE,
            data: {
              event: {
                name: EventNames.click,
                category: 'cinch_servicing',
                action: 'open',
                label: 'marshall-explanation',
                type: 'drawer',
              },
            },
          })
          setShowMarshallExplanationDrawer(true)
        }}
      >
        Marshall
      </TextLink>{' '}
      stores.
    </Text>
  ) : (
    <Text>
      Enter your postcode to find your nearest{' '}
      <TextLink
        onClick={() => {
          trackEvent({
            eventName: `Marshall explanation drawer opened`,
            type: TrackingEventTypes.ADOBE,
            data: {
              event: {
                name: EventNames.click,
                category: 'cinch_servicing',
                action: 'open',
                label: 'marshall-explanation',
                type: 'drawer',
              },
            },
          })
          setShowMarshallExplanationDrawer(true)
        }}
      >
        service centre
      </TextLink>{' '}
      from over 100 locations across the country.
    </Text>
  )

  return (
    <CarMaintenanceStepWrapper
      title="Find a service centre"
      descriptionElement={descriptionElement}
      showContinueButton={false}
    >
      <FindAStore
        initialPostcode={ctxPostCode}
        mobileInitialVisibleCount={1}
        under60Months={under60Months}
        vehicleMake={vehicleMake}
        onSelectStore={selectStore}
        onNearestOemStoreChange={handleNearestOemStoreChange}
        renderListBanner={({
          nearestMatchingStoreWithin20Miles,
          storesWithDistance,
          visibleStores,
        }) =>
          under60Months &&
          nearestMatchingStoreWithin20Miles &&
          visibleStores.length > 0 ? (
            <div className={styles.oemAlert}>
              <Text
                fontSize="m"
                fontWeight="bold"
                className={styles.oemAlertHeading}
              >
                Manufacturer-approved servicing
              </Text>
              <Text fontSize="s" className={styles.oemAlertText}>
                Since your car&apos;s less than 5 years old, a manufacturer
                service could help protect your warranty.
              </Text>
              <Text fontSize="s" className={styles.oemAlertText}>
                Your nearest {vehicleMake} service centre is{' '}
                {toMiles(nearestMatchingStoreWithin20Miles.distance)} miles
                away.
              </Text>
              <Secondary
                isFullWidth
                size="medium"
                onClick={() => {
                  trackEvent({
                    eventName: `Manufacturer approved drawer opened`,
                    type: TrackingEventTypes.ADOBE,
                    data: {
                      event: {
                        name: EventNames.click,
                        category: 'cinch_servicing',
                        action: 'find_out_more_OEM_options',
                        label: 'make_a_booking_OEM_options',
                        type: storesWithDistance[0].store,
                      },
                    },
                  })
                  setShowManufacturerApprovedDrawer(true)
                }}
              >
                Find out more
              </Secondary>
            </div>
          ) : null
        }
      />
    </CarMaintenanceStepWrapper>
  )
}
